/**
 * Step 1 of the catering wizard — customer + event details.
 * Extracted from CateringSection.jsx. Stateless: takes the form dict and
 * onChange handler from the parent.
 */
import React from "react";
import { CalendarIcon, ArrowRight } from "lucide-react";
import { format } from "date-fns";
import { es as esLocale, enUS } from "date-fns/locale";
import { Calendar } from "../ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { useApp } from "../../AppContext";
import { CField } from "./CateringPrimitives";

const EVENT_TYPES = ["cumpleanos", "boda", "bautizo", "quinceanera", "corporativo", "otro"];

const CateringDetailsStep = ({
    form,
    onChange,
    onSubmit,
    minLeadHours,
    minEventDate,
    hasPreorderInSelections,
}) => {
    const { t, lang } = useApp();
    return (
        <form onSubmit={onSubmit} data-testid="catering-form" className="space-y-5">
            <div
                data-testid="catering-confirmation-notice"
                className="p-4 rounded-2xl flex items-start gap-3"
                style={{ background: "#fdf6ee", border: "1px solid #ecb087" }}
            >
                <div className="w-9 h-9 rounded-full bg-terracotta-500 text-white flex items-center justify-center shrink-0 font-bold">
                    ✓
                </div>
                <div className="text-sm">
                    <div className="font-bold text-earth-900">
                        {lang === "es"
                            ? "Solicitud sujeta a confirmación"
                            : "Request subject to confirmation"}
                    </div>
                    <div className="text-earth-700 mt-0.5 text-xs leading-relaxed">
                        {lang === "es"
                            ? `Te confirmaremos la cotización por WhatsApp al número que dejes. Mínimo ${minLeadHours} horas de anticipación${hasPreorderInSelections ? " (incluye pre-orden 72h)" : ""}.`
                            : `We'll confirm the quote via WhatsApp on the number you provide. Minimum ${minLeadHours} hours notice${hasPreorderInSelections ? " (includes 72h pre-order)" : ""}.`}
                    </div>
                </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-5">
                <CField label={t.catering.name} value={form.customer_name} onChange={onChange("customer_name")} required testId="catering-name" />
                <CField label={t.catering.phone} value={form.customer_phone} onChange={onChange("customer_phone")} required testId="catering-phone" />
            </div>
            <CField label={t.catering.email} type="email" value={form.customer_email} onChange={onChange("customer_email")} testId="catering-email" />

            <div className="grid sm:grid-cols-2 gap-5">
                <div>
                    <label className="block text-xs uppercase tracking-wider font-bold text-earth-600 mb-2">
                        {t.catering.date}
                        <span className="text-terracotta-500"> *</span>
                    </label>
                    <Popover>
                        <PopoverTrigger asChild>
                            <button
                                type="button"
                                data-testid="catering-date-btn"
                                className="w-full bg-earth-50 border border-earth-200 rounded-xl px-4 py-3 outline-none focus:border-terracotta-500 flex items-center gap-2 text-left"
                            >
                                <CalendarIcon className="w-4 h-4 text-earth-500" />
                                <span className={form.event_date ? "text-earth-900" : "text-earth-400"}>
                                    {form.event_date
                                        ? format(form.event_date, "PPP", { locale: lang === "es" ? esLocale : enUS })
                                        : lang === "es" ? "Selecciona fecha" : "Pick a date"}
                                </span>
                            </button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                                mode="single"
                                selected={form.event_date}
                                onSelect={onChange("event_date")}
                                disabled={(d) => d < new Date(minEventDate.toDateString())}
                                initialFocus
                            />
                        </PopoverContent>
                    </Popover>
                </div>

                <div>
                    <label className="block text-xs uppercase tracking-wider font-bold text-earth-600 mb-2">
                        {t.catering.type}
                    </label>
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <button
                                type="button"
                                data-testid="catering-type-btn"
                                className="w-full bg-earth-50 border border-earth-200 rounded-xl px-4 py-3 outline-none focus:border-terracotta-500 flex items-center justify-between"
                            >
                                <span>{t.catering.types[form.event_type]}</span>
                                <span className="text-earth-400">▾</span>
                            </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="min-w-[200px]">
                            {EVENT_TYPES.map((et) => (
                                <DropdownMenuItem
                                    key={et}
                                    data-testid={`catering-type-${et}-btn`}
                                    onClick={() => onChange("event_type")(et)}
                                >
                                    {t.catering.types[et]}
                                </DropdownMenuItem>
                            ))}
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-5">
                <CField label={t.catering.guests} type="number" value={form.guest_count} onChange={onChange("guest_count")} testId="catering-guests" />
                <CField label={t.catering.budget} type="number" value={form.budget} onChange={onChange("budget")} testId="catering-budget" />
            </div>

            <div>
                <label className="block text-xs uppercase tracking-wider font-bold text-earth-600 mb-2">
                    {t.catering.notes}
                </label>
                <textarea
                    data-testid="catering-notes"
                    value={form.notes}
                    onChange={(e) => onChange("notes")(e.target.value)}
                    rows={3}
                    className="w-full bg-earth-50 border border-earth-200 rounded-xl px-4 py-3 outline-none focus:border-terracotta-500"
                />
            </div>

            <button type="submit" data-testid="catering-next-btn" className="btn-primary w-full mt-2">
                {lang === "es" ? "Siguiente: elegir menú" : "Next: choose menu"}
                <ArrowRight className="w-4 h-4" />
            </button>
        </form>
    );
};

export default CateringDetailsStep;
